import * as React from 'react';
import {
    Alert, Autocomplete, Box, Button, Checkbox, FormControlLabel, MenuItem,
    Paper, Stack, TextField, Typography
} from '@mui/material';
import { FgsManagement } from '../../controllers';
import { TokenValidator } from '../../utils';

const initial = {client: '', product: '', code: '', template: '', additionalLabel: false, carbonType: 'hollow', reason: ''};
const correlationId = () => window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : ((r & 3) | 8)).toString(16);
    });

export default function NewClientSetup() {
    const [catalog, setCatalog] = React.useState({clients: [], products: [], templates: []});
    const [form, setForm] = React.useState({...initial});
    const [busy, setBusy] = React.useState(false);
    const [message, setMessage] = React.useState(null);
    const token = () => TokenValidator.GetInternalUser();

    React.useEffect(() => {
        (async () => {
            const response = await FgsManagement.List(await token());
            if (response && response.success) setCatalog(response.result);
            else setMessage({severity: 'error', text: response ? response.message : 'Unable to load setup catalogs'});
        })();
    }, []);

    const save = async () => {
        if (!form.client.trim() || !form.product || !form.code.trim() || !form.template) {
            return setMessage({severity: 'error', text: 'Client, product, FGS code and template are required.'});
        }
        if (form.reason.trim().length < 3) return setMessage({severity: 'error', text: 'A reason of at least 3 characters is required.'});
        setBusy(true);
        const response = await FgsManagement.Create(await token(), {
            ...form,
            configurePrintRoute: true,
            active: true,
            correlationId: correlationId()
        });
        setBusy(false);
        if (!response || !response.success) {
            return setMessage({severity: 'error', text: response ? response.message : 'Unable to create client setup'});
        }
        setMessage({severity: 'success', text: 'Client FGS assignment and Carbon route were created together.'});
        setForm({...initial});
    };

    return <Box>
        {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{mb: 2}}>{message.text}</Alert>}
        <Alert severity='warning' sx={{mb: 2}}>
            Saving an active route may release matching orders waiting at G-Code Complete on the next Controller cycle.
        </Alert>
        <Paper sx={{p: 3, maxWidth: 760}}>
            <Typography variant='h6' sx={{mb: 2}}>New Client Setup</Typography>
            <Stack gap={2}>
                <Autocomplete freeSolo options={catalog.clients.map(item => item.name)} value={form.client || null} inputValue={form.client}
                    onChange={(_, value) => setForm({...form, client: value || ''})}
                    onInputChange={(_, value) => setForm({...form, client: value})}
                    renderInput={params => <TextField {...params} label='Client' inputProps={{...params.inputProps, maxLength: 100}}
                        helperText="Use the exact case_client name. Spaces and letter case are normalized for routing." />} />
                <TextField select label='Product / condition' value={form.product} onChange={e => setForm({...form, product: e.target.value})}>
                    {catalog.products.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <TextField label='FGS code' value={form.code} inputProps={{maxLength: 40}}
                    helperText='The optional FGS- prefix is normalized.' onChange={e => setForm({...form, code: e.target.value})} />
                <TextField select label='ZPL template' value={form.template} onChange={e => setForm({...form, template: e.target.value})}>
                    {catalog.templates.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
                </TextField>
                <FormControlLabel control={<Checkbox checked={form.additionalLabel}
                    onChange={e => setForm({...form, additionalLabel: e.target.checked})} />} label='Print additional FGS label at bagging' />
                <TextField label='Print destination' value='Carbon' InputProps={{readOnly: true}} />
                <TextField select label='Carbon type' value={form.carbonType} onChange={e => setForm({...form, carbonType: e.target.value})}>
                    <MenuItem value='hollow'>Hollow</MenuItem><MenuItem value='solid'>Solid</MenuItem>
                </TextField>
                <TextField required multiline minRows={2} label='Reason' value={form.reason} inputProps={{maxLength: 500}}
                    onChange={e => setForm({...form, reason: e.target.value})} />
                <Button variant='contained' onClick={save} disabled={busy}>Create client configuration</Button>
            </Stack>
        </Paper>
        <Alert severity='info' sx={{mt: 2, maxWidth: 760}}>
            Add further products for the same client from FGS Assignments. The Carbon route is shared by the client.
        </Alert>
    </Box>;
}
